import { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Container, Stack, TextField, Typography } from '@mui/material';
import { getRuntimeConfig } from '../../app/runtimeConfig';
import { useSession } from '../../app/session';
import BrandLogo from '../../components/BrandLogo';

export default function LoginScreen() {
  const runtime = getRuntimeConfig();
  const { authConfig, signInWithCognito, signInWithPassword, requestPasswordReset, confirmPasswordReset, authError } = useSession();
  const [email, setEmail] = useState('williedejongh@icloud.com');
  const [password, setPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState<'password' | 'oauth' | 'forgot' | 'confirm' | ''>('');
  const [resetRequested, setResetRequested] = useState(false);
  const [localError, setLocalError] = useState('');
  const [localNotice, setLocalNotice] = useState('');

  const showHostedLogin = runtime.stage === 'production' && Boolean(authConfig?.cognitoDomain);
  const showSandboxPasswordLogin = runtime.stage === 'sandbox';

  const submitPassword = async () => {
    setLoading('password');
    setLocalError('');
    setLocalNotice('');
    try {
      await signInWithPassword(email, password);
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Unable to sign in.');
    } finally {
      setLoading('');
    }
  };

  const submitHostedLogin = async () => {
    setLoading('oauth');
    setLocalError('');
    setLocalNotice('');
    try {
      await signInWithCognito();
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Unable to start hosted sign-in.');
      setLoading('');
    }
  };

  const submitForgot = async () => {
    setLoading('forgot');
    setLocalError('');
    setLocalNotice('');
    try {
      await requestPasswordReset(email);
      setResetRequested(true);
      setLocalNotice('Password reset code sent. Check your email.');
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Unable to request password reset.');
    } finally {
      setLoading('');
    }
  };

  const submitConfirmReset = async () => {
    setLoading('confirm');
    setLocalError('');
    setLocalNotice('');
    try {
      await confirmPasswordReset(email, resetCode, newPassword);
      setResetRequested(false);
      setResetCode('');
      setNewPassword('');
      setLocalNotice('Password was reset. You can now sign in.');
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : 'Unable to confirm password reset.');
    } finally {
      setLoading('');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at top left, #efe3c2, #d6e3eb 56%, #f8fafb 100%)', py: 8 }}>
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 4, boxShadow: '0 24px 64px rgba(15, 23, 42, 0.12)' }}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box>
                <Box sx={{ mb: 2 }}>
                  <BrandLogo variant="full" height={72} />
                </Box>
                <Chip label={`Stage: ${runtime.stage}`} sx={{ mb: 2, fontWeight: 700 }} />
                <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.04em' }}>
                  Operator Console
                </Typography>
              </Box>

              {authError ? <Alert severity="warning">{authError}</Alert> : null}
              {localError ? <Alert severity="error">{localError}</Alert> : null}
              {localNotice ? <Alert severity="success">{localNotice}</Alert> : null}

              {showHostedLogin ? (
                <Button variant="contained" size="large" onClick={() => void submitHostedLogin()} disabled={loading !== ''}>
                  {loading === 'oauth' ? 'Redirecting...' : 'Sign In With Cognito'}
                </Button>
              ) : null}

              {showSandboxPasswordLogin ? (
                <>
                  <TextField label="Email" value={email} onChange={(event) => setEmail(event.target.value)} fullWidth />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    fullWidth
                  />

                  <Button variant="contained" size="large" onClick={() => void submitPassword()} disabled={loading !== '' || !email || !password}>
                    {loading === 'password' ? 'Signing In...' : 'Sign In'}
                  </Button>
                  <Button variant="text" onClick={() => void submitForgot()} disabled={loading !== '' || !email}>
                    {loading === 'forgot' ? 'Sending Reset Code...' : 'Forgot Password'}
                  </Button>

                  {resetRequested ? (
                    <>
                      <TextField
                        label="Reset Code"
                        value={resetCode}
                        onChange={(event) => setResetCode(event.target.value)}
                        fullWidth
                      />
                      <TextField
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(event) => setNewPassword(event.target.value)}
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => void submitConfirmReset()}
                        disabled={loading !== '' || !resetCode || !newPassword}
                      >
                        {loading === 'confirm' ? 'Confirming...' : 'Confirm Password Reset'}
                      </Button>
                    </>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
